import {
  NOTIFICATION_LIST_CATEGORIES,
  NOTIFICATION_LIST_SEVERITIES,
} from '../../contracts/notification-list-policy.generated';
import { billingAuditActionLabel } from '../../shared/list-delivery/operational-list-labels';
import { BILLING_AUDIT_ACTIONS } from '../../contracts/operational-list-policy.generated';
import { REPORTING_POLICY } from '../../contracts/reporting-policy.generated';
import { REPORT_PERIOD_LABELS } from '../../shared/reporting/metrics-report';
import { headerDownloadTopic } from '../export-button/header-download';
import { growthListStatusValues } from '../../contracts/growth-list-policy.generated';
import { COUNTRY_LAUNCH_PACKAGES } from '../../contracts/country-launch-packages.generated';
import {
  COLLAB_TRANSACTION_STATUSES,
  ORDER_TRANSACTION_STATUSES,
} from '../../contracts/ads-transaction-list-policy.generated';
import {
  Component,
  OnInit,
  Input,
  SimpleChanges,
  OnDestroy,
  inject,
  OnChanges,
  NgZone,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonSearchbar, IonButton, IonButtons, IonIcon, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { Events } from '../../services/events.service';
import { Subscription } from 'rxjs';
import { ReportFilterModalComponent } from '../report-filter-modal/report-filter-modal.component';
import { AuditLogFilterModalComponent } from '../audit-log-filter-modal/audit-log-filter-modal.component';
import { ComplianceStatusFilterModalComponent } from '../compliance-status-filter-modal/compliance-status-filter-modal.component';
import { UserFilterModalComponent } from '../user-filter-modal/user-filter-modal.component';
import { SupportRequestFilterModalComponent } from '../support-request-filter-modal/support-request-filter-modal.component';
import { LogFilterModalComponent } from '../log-filter-modal/log-filter-modal.component';
import { PublicContentFilterModalComponent } from '../public-content-filter-modal/public-content-filter-modal.component';
import { PostsFilterModalComponent } from '../../posts/posts-filter-modal.component';
import { StoreFilterModalComponent } from '../../store/store-filter-modal.component';
import {
  OperationsMetricFilterModalComponent,
  OperationsMetricStatusOption,
} from '../operations-metric-filter-modal/operations-metric-filter-modal.component';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, IonToolbar, IonButton, IonButtons, IonIcon, IonSearchbar],
})
export class SearchComponent implements OnInit, OnChanges, OnDestroy {
  private modalCtrl: ModalController = inject(ModalController);
  private readonly zone = inject(NgZone);
  private readonly changeDetector = inject(ChangeDetectorRef, { optional: true });
  private downloadStateRevision = 0;
  private destroyed = false;
  public events: Events = inject(Events);

  @Input() grobalSearch: any = false;
  @Input() userId: any;
  @Input() recordId: any = '';
  @Input() title: any = '';
  @Input() pageSearch: any = '';
  @Input() filterType: 'posts' | 'store' | '' = '';
  @Input() segmentSearch: any = '';
  @Input() placeholderText: any;
  @Input() showFilter: any;
  @Input() showAdd: any = false;
  @Input() showDownload: any = false;
  @Input() isModal: any = false;
  @Input() pageType: any = '';
  @Input() passedUsers: any = [];
  @Input() passedData: any = {};
  public subscription!: Subscription;
  private filterSubscription?: Subscription;
  private downloadStateSubscription?: Subscription;
  headerDownloadDisabled = false;
  headerDownloadExporting = false;
  private currentFiltersByPage: Record<string, any> = {};
  public tab: any = 'users';
  searchText: string = '';
  globalSearchText: string = '';

  ngOnChanges(): void {
    this.bindDownloadState();
  }

  private bindDownloadState(): void {
    this.downloadStateSubscription?.unsubscribe();
    ++this.downloadStateRevision;
    this.headerDownloadDisabled = false;
    this.headerDownloadExporting = false;
    if (!this.showDownload) return;
    this.downloadStateSubscription = this.events.subscribeRetained(
      headerDownloadTopic(this.pageSearch, this.segmentSearch),
      (state) => {
        const revision = ++this.downloadStateRevision;
        // The exporting sibling can publish from ngOnChanges after this header
        // has been checked. Defer the binding update, then notify Angular even
        // when the query/export promise completed outside its zone.
        queueMicrotask(() => {
          if (this.destroyed || revision !== this.downloadStateRevision) return;
          this.zone.run(() => {
            this.headerDownloadDisabled = !!state?.disabled;
            this.headerDownloadExporting = !!state?.exporting;
            this.changeDetector?.markForCheck();
          });
        });
      }
    );
  }

  ngOnInit() {
    this.bindDownloadState();
    this.subscription = this.events.currentSearchData.subscribe((data) => {
      if (data) {
        this.searchText = data.string || '';
      }
    });
    this.filterSubscription = this.events.currentFilterData.subscribe((data) => {
      if (!data?.page) return;
      this.currentFiltersByPage[this.getFilterKey(data.page, data.segment)] = data.clear ? null : data.filters || null;
    });
  }

  get canShowFilter(): boolean {
    return this.showFilter;
  }

  private get filterKey(): string {
    return this.getFilterKey(this.pageSearch, this.segmentSearch);
  }

  private getFilterKey(page: string, segment?: string): string {
    return `${page || ''}:${segment || ''}`;
  }

  async openFilterModal() {
    if (this.pageSearch === 'users') {
      await this.openUserFilterModal();
      return;
    }

    if (this.pageSearch === 'user-reports') {
      await this.openReportFilterModal();
      return;
    }

    if (this.pageSearch === 'support-requests') {
      await this.openSupportRequestFilterModal();
      return;
    }

    if (this.pageSearch === 'audit-logs') {
      await this.openAuditLogFilterModal();
      return;
    }

    if (this.pageSearch === 'logs') {
      await this.openLogFilterModal();
      return;
    }

    if (this.pageSearch === 'compliance-status') {
      await this.openComplianceStatusFilterModal();
      return;
    }

    if (this.filterType === 'posts') {
      await this.openPostsFilterModal();
      return;
    }

    if (this.filterType === 'store') {
      await this.openStoreFilterModal();
      return;
    }

    if (this.isPublicContentFilterPage(this.pageSearch)) {
      await this.openPublicContentFilterModal();
      return;
    }

    if (this.isOperationsMetricFilterPage(this.pageSearch)) {
      await this.openOperationsMetricFilterModal();
      return;
    }

    this.events.publish('open-filter', { page: this.pageSearch });
  }

  async openPublicContentFilterModal() {
    const filterKey = this.getFilterKey(this.pageSearch);
    const modal = await this.modalCtrl.create({
      component: PublicContentFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        title: `${this.title || 'Content'} Filters`,
        filters: this.currentFiltersByPage[filterKey],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: this.pageSearch, clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: this.pageSearch, filters: data.filters });
      }
    });

    await modal.present();
  }

  async openPostsFilterModal() {
    const filterKey = this.getFilterKey(this.pageSearch);
    const modal = await this.modalCtrl.create({
      component: PostsFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[filterKey],
        includePreviewModes: [
          'list-patterns',
          'advanced-list-pattern',
          'grid-list-pattern',
          'advanced-grid-pattern',
        ].includes(this.pageSearch),
        includeFeedTypes: this.pageSearch === 'posts',
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: this.pageSearch, clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: this.pageSearch, filters: data.filters });
      }
    });

    await modal.present();
  }

  async openStoreFilterModal() {
    const filterKey = this.getFilterKey(this.pageSearch);
    const modal = await this.modalCtrl.create({
      component: StoreFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[filterKey],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: this.pageSearch, clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: this.pageSearch, filters: data.filters });
      }
    });

    await modal.present();
  }

  private isPublicContentFilterPage(page: string): boolean {
    return [
      'edit-articles',
      'edit-blogs',
      'edit-news',
      'edit-jobs',
      'edit-termsofuse',
      'edit-privacypolicy',
      'edit-aup',
      'edit-copyright',
      'edit-community-guidelines',
    ].includes(page);
  }

  async openUserFilterModal() {
    const filterKey = this.getFilterKey(this.pageSearch, this.segmentSearch);
    const modal = await this.modalCtrl.create({
      component: UserFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[filterKey],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: this.pageSearch, segment: this.segmentSearch, clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: this.pageSearch, segment: this.segmentSearch, filters: data.filters });
      }
    });

    await modal.present();
  }

  async openReportFilterModal() {
    const modal = await this.modalCtrl.create({
      component: ReportFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[this.getFilterKey('user-reports')],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: 'user-reports', clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: 'user-reports', filters: data.filters });
      }
    });

    await modal.present();
  }

  async openSupportRequestFilterModal() {
    const modal = await this.modalCtrl.create({
      component: SupportRequestFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[this.getFilterKey('support-requests')],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: 'support-requests', clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: 'support-requests', filters: data.filters });
      }
    });

    await modal.present();
  }

  async openComplianceStatusFilterModal() {
    const modal = await this.modalCtrl.create({
      component: ComplianceStatusFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[this.getFilterKey('compliance-status')],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: 'compliance-status', clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: 'compliance-status', filters: data.filters });
      }
    });

    await modal.present();
  }

  async openAuditLogFilterModal() {
    const modal = await this.modalCtrl.create({
      component: AuditLogFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[this.getFilterKey('audit-logs')],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: 'audit-logs', clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: 'audit-logs', filters: data.filters });
      }
    });

    await modal.present();
  }

  async openLogFilterModal() {
    const modal = await this.modalCtrl.create({
      component: LogFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        filters: this.currentFiltersByPage[this.getFilterKey('logs')],
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: 'logs', clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: 'logs', filters: data.filters });
      }
    });

    await modal.present();
  }

  async openOperationsMetricFilterModal() {
    const filterKey = this.getFilterKey(this.pageSearch, this.segmentSearch);
    const modal = await this.modalCtrl.create({
      component: OperationsMetricFilterModalComponent,
      cssClass: 'filter-modal',
      componentProps: {
        title: `${this.title || (this.pageSearch === 'investors' ? 'Investors' : this.pageSearch === 'payout-requests' ? 'Payouts' : this.pageSearch === 'company-earnings' ? 'Income' : this.pageSearch === 'billing-ops' ? 'Billing audit' : this.pageSearch === 'admin-notifications' ? 'Notifications' : 'Page')} Filters`,
        filters:
          this.currentFiltersByPage[filterKey] ||
          (this.pageSearch === 'store-screenshot-studio' ? { status: 'active', sortMode: 'newest' } : undefined),
        showReportYear: this.pageSearch === 'company-earnings',
        statusLabel: this.pageSearch === 'billing-ops' ? 'Action' : 'Status',
        statusOptions: this.getOperationsStatusOptions(this.pageSearch, this.segmentSearch),
        sortOptions: this.getListSortOptions(this.pageSearch, this.segmentSearch),
        categoryOptions:
          this.pageSearch === 'admin-notifications'
            ? [
                { value: 'all', label: 'All categories' },
                ...NOTIFICATION_LIST_CATEGORIES.map((value) => ({
                  value,
                  label: value
                    .toLowerCase()
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase()),
                })),
              ]
            : [],
        severityOptions:
          this.pageSearch === 'admin-notifications'
            ? [
                { value: 'all', label: 'All severities' },
                ...NOTIFICATION_LIST_SEVERITIES.map((value) => ({
                  value,
                  label: value[0] + value.slice(1).toLowerCase(),
                })),
              ]
            : [],
        currencyOptions:
          this.isMarketplaceTransactionList(this.pageSearch, this.segmentSearch) ||
          this.pageSearch === 'payout-requests'
            ? [
                { value: '', label: 'All currencies' },
                ...COUNTRY_LAUNCH_PACKAGES.filter((c) => c.status === 'active').map((c) => ({
                  value: c.currencyCode,
                  label: c.currencyCode,
                })),
                { value: 'UNKNOWN', label: 'Unknown currency' },
              ]
            : [],
        presetOptions: this.getOperationsPresetOptions(this.pageSearch, this.segmentSearch),
        defaultPreset:
          this.pageSearch === 'admin-revenue'
            ? this.segmentSearch === 'chargebacks'
              ? 'all'
              : this.segmentSearch === 'transactions'
                ? 'last30'
                : 'today'
            : this.pageSearch === 'payout-requests'
              ? 'all'
              : 'last30',
        showDateRange: this.usesOperationsDateRange(this.pageSearch),
        showCustomRange: ['admin-revenue', 'payout-requests'].includes(this.pageSearch),
      },
    });

    modal.onDidDismiss().then((res) => {
      const data = res.data;
      if (data?.clear) {
        this.publishFilterData({ page: this.pageSearch, segment: this.segmentSearch, clear: true });
      } else if (data?.filters) {
        this.publishFilterData({ page: this.pageSearch, segment: this.segmentSearch, filters: data.filters });
      }
    });

    await modal.present();
  }

  private isMarketplaceTransactionList(page: string, segment?: string): boolean {
    return (
      ['marketplace-ads', 'marketplace-collabs', 'marketplace-orders', 'marketplace-rentals'].includes(page) &&
      segment === 'transactions'
    );
  }

  private getListSortOptions(page: string, segment?: string): OperationsMetricStatusOption[] {
    const dates = [
      { value: 'newest', label: 'Newest first' },
      { value: 'oldest', label: 'Oldest first' },
    ];
    if (['investors', 'payout-requests', 'store-screenshot-studio'].includes(page))
      return [...dates, { value: 'nameAsc', label: 'Name A–Z' }, { value: 'nameDesc', label: 'Name Z–A' }];
    if (['subscriptions', 'referrals', 'promos'].includes(page))
      return [...dates, { value: 'name', label: 'Name / code A–Z' }];
    if (
      this.isMarketplaceTransactionList(page, segment) ||
      page === 'marketplace-metric-details' ||
      (page === 'admin-revenue' && ['transactions', 'chargebacks'].includes(segment || '')) ||
      (page === 'revenue-ledger' && segment === 'daily') ||
      ['company-earnings', 'billing-ops', 'admin-notifications'].includes(page)
    )
      return dates;
    return [];
  }

  private isOperationsMetricFilterPage(page: string): boolean {
    return [
      'marketplace-ads',
      'marketplace-collabs',
      'marketplace-orders',
      'marketplace-rentals',
      'revenue-ledger',
      'company-earnings',
      'billing-ops',
      'admin-notifications',
      'admin-revenue',
      'payout-requests',
      'investors',
      'store-screenshot-studio',
      'marketplace-metric-details',
      'subscriptions',
      'referrals',
      'promos',
    ].includes(page);
  }

  private getOperationsPresetOptions(page: string, segment?: string): OperationsMetricStatusOption[] | undefined {
    if (page === 'payout-requests')
      return [
        { value: 'all', label: 'All dates' },
        ...REPORTING_POLICY.periods
          .filter((period) => period !== 'asOf' && period !== 'all')
          .map((period) => ({ value: period, label: REPORT_PERIOD_LABELS[period] || period })),
      ];
    if (page !== 'admin-revenue') return undefined;
    return [
      ...(segment === 'chargebacks' ? [{ value: 'all', label: 'All dates' }] : []),
      ...(segment === 'transactions' ? [{ value: 'last30', label: 'Last 30 days' }] : []),
      { value: 'today', label: 'Today' },
      { value: 'week', label: 'This Week' },
      { value: 'month', label: 'This Month' },
      { value: 'custom', label: 'Custom Range' },
    ];
  }

  private usesOperationsDateRange(page: string): boolean {
    return [
      'payout-requests',
      'marketplace-ads',
      'marketplace-collabs',
      'marketplace-orders',
      'marketplace-rentals',
      'revenue-ledger',
      'admin-revenue',
    ].includes(page);
  }

  private getOperationsStatusOptions(page: string, segment?: string): OperationsMetricStatusOption[] {
    if (page === 'admin-notifications')
      return [
        { value: 'all', label: 'All statuses' },
        { value: 'unread', label: 'Unread' },
        { value: 'read', label: 'Read' },
        { value: 'acknowledged', label: 'Acknowledged' },
      ];
    if (page === 'marketplace-metric-details') return [];
    if (page === 'store-screenshot-studio')
      return [
        { value: 'active', label: 'Active' },
        { value: 'archived', label: 'Archived' },
      ];
    const common = [
      { value: 'all', label: 'All' },
      { value: 'pending', label: 'Pending' },
      { value: 'completed', label: 'Completed' },
    ];

    if (page === 'investors' || page === 'payout-requests') {
      const statuses =
        page === 'investors'
          ? ['all', 'new', 'contacted', 'archived', 'marked', 'unread']
          : [
              'all',
              'pending',
              'requested',
              'submitted',
              'pending_approval',
              'pending_second_approval',
              'approved',
              'executing',
              'executed',
              'reconciled',
              'processing',
              'paid',
              'completed',
              'rejected',
              'failed',
              'execution_failed',
              'cancelled',
              'canceled',
              'marked',
              'unread',
            ];
      return statuses.map((value) => ({
        value,
        label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      }));
    }
    if (page === 'billing-ops') {
      let actions: string[] = [...BILLING_AUDIT_ACTIONS];
      const subscription = this.events.subscribeRetained('billing-ops:audit-actions', (values) => {
        if (Array.isArray(values)) actions = values;
      });
      subscription.unsubscribe();
      return [
        { value: 'all', label: 'All actions' },
        ...actions.map((value) => ({ value, label: billingAuditActionLabel(value) })),
      ];
    }
    switch (page) {
      case 'admin-revenue':
        if (segment === 'chargebacks') {
          return [
            { value: 'all', label: 'All' },
            { value: 'open', label: 'Open' },
            { value: 'pending', label: 'Pending' },
            { value: 'resolved', label: 'Resolved' },
          ];
        }
        return [
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'complete', label: 'Completed' },
          { value: 'failed', label: 'Failed' },
          { value: 'chargedback', label: 'Chargedback' },
        ];
      case 'marketplace-ads':
        return [
          'all',
          'active',
          'pending',
          'expired',
          'paused',
          'completed',
          'failed',
          'rejected',
          'processing',
          'succeeded',
          'canceled',
          'unknown',
        ].map((value) => ({ value, label: value === 'all' ? 'All' : value.charAt(0).toUpperCase() + value.slice(1) }));
      case 'marketplace-collabs':
        if (segment === 'transactions')
          return ['all', ...COLLAB_TRANSACTION_STATUSES].map((value) => ({
            value,
            label: value.replace(/\b\w/g, (c) => c.toUpperCase()),
          }));
        return [{ value: 'all', label: 'All' }, { value: 'active', label: 'Active' }, ...common.slice(1)];
      case 'marketplace-orders':
        if (segment === 'transactions')
          return ['all', ...ORDER_TRANSACTION_STATUSES].map((value) => ({
            value,
            label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          }));
        return [
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'processing', label: 'Processing' },
          { value: 'shipped', label: 'Shipped' },
          { value: 'delivered', label: 'Delivered' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'refunded', label: 'Refunded' },
        ];
      case 'marketplace-rentals':
        if (segment === 'transactions')
          return ['all', ...ORDER_TRANSACTION_STATUSES].map((value) => ({
            value,
            label: value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
          }));
        return [
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'confirmed', label: 'Confirmed' },
          { value: 'active', label: 'Active' },
          { value: 'completed', label: 'Completed' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'refunded', label: 'Refunded' },
        ];
      case 'payout-requests':
        return [
          { value: 'all', label: 'All' },
          { value: 'pending', label: 'Pending' },
          { value: 'approved', label: 'Approved' },
          { value: 'rejected', label: 'Rejected' },
          { value: 'marked', label: 'Starred' },
        ];
      case 'subscriptions':
        return [
          { value: 'all', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'cancelled', label: 'Cancelled' },
          { value: 'past_due', label: 'Past Due' },
          { value: 'trialing', label: 'Trialing' },
          { value: 'complimentary', label: 'Complimentary' },
        ];
      case 'referrals':
        return [
          { value: 'all', label: 'All' },
          { value: 'attributed', label: 'Attributed' },
          { value: 'rewarded', label: 'Rewarded' },
          { value: 'pending_reward', label: 'Pending Reward' },
        ];
      case 'promos':
        return growthListStatusValues('promos').map((value) => ({
          value,
          label: value.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
        }));
      default:
        return [];
    }
  }

  searchBydatatype(event: any) {
    const value = event.target.value || '';
    if (this.grobalSearch) {
      this.globalSearchText = value;
      this.globalSearchData({ string: value });
    } else {
      this.searchText = value;
      this.events.updateSearchData({
        string: value,
        ...(this.pageSearch === 'admin-notifications' ? { page: this.pageSearch } : {}),
      });
      if (this.pageSearch) {
        this.events.publishRetained(`${this.pageSearch}:search`, {
          page: this.pageSearch,
          ...(this.segmentSearch ? { segment: this.segmentSearch } : {}),
          string: value,
        });
      }
    }
  }

  private publishFilterData(data: any) {
    this.events.updateFilterData(data);
    if (data?.page) this.events.publishRetained(`${data.page}:filters`, data);
  }

  globalSearchData(data: any) {}

  onAddClick() {
    this.events.updateaddMsgData({ add: true, page: this.pageSearch });
  }

  onDownloadClick() {
    if (this.headerDownloadDisabled) return;
    this.events.updateDownloadData({ download: true, page: this.pageSearch, segment: this.segmentSearch });
  }

  segmentChanged(ev: any) {
    this.tab = ev.detail.value;
    if (this.tab === 'users') {
      this.placeholderText = 'Search follower with name or userName';
    } else if (this.tab === 'groups') {
      this.placeholderText = 'Search Groups';
    }
  }

  done() {
    this.dismiss();
  }

  dismiss() {
    this.modalCtrl.dismiss({
      dismissed: true,
    });
  }

  ngOnDestroy() {
    this.destroyed = true;
    ++this.downloadStateRevision;
    this.downloadStateSubscription?.unsubscribe();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    this.events.updateSearchData(null);
  }
}
